import {useQuery} from '@tanstack/react-query';
import * as I from 'immutable';
import React from 'react';
import {useLocation} from 'react-router-dom';

import {PushNotificationFn} from 'app/components/Notification';
import api from 'app/modules/Remote/api';
import {ApiProjectReport} from 'app/modules/Remote/Feature';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';

import cs from '../PropertyOverview.module.scss';
import {useSyncTableStateToUrl} from '../tableUrlState';
import PortfolioReportsControls from './PortfolioReportsControls';
import PortfolioReportsTable, {ReportsStatus} from './PortfolioReportsTable';
import ReportDeleteDialog from './ReportDeleteDialog';
import ReportExportDialog from './ReportExportDialog';
import {searchWithTableState, tableStateFromSearch} from './reportsTableState';
import {usePortfolioReportsTable} from './usePortfolioReportsTable';

const NO_REPORTS: ApiProjectReport[] = [];

export function useProjectReports(projectId: string) {
  return useQuery({
    queryKey: ['projectReports', projectId],
    queryFn: async () => {
      const response = await api.projectReports(projectId).getList();
      return response.get('data').toJS() as ApiProjectReport[];
    },
  });
}

export interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  selectedProject: I.ImmutableOf<ApiProject>;
  pushNotification: PushNotificationFn;
  /** Rendered first in the controls row. */
  viewToggle?: React.ReactNode;
}

/**
 * Every saved report across the portfolio's properties, with selection for
 * bulk PDF export and deletion. Filters and sort live in the query string so
 * the view is bookmarkable.
 */
const PortfolioReports: React.FunctionComponent<Props> = ({
  organization,
  profile,
  selectedProject,
  pushNotification,
  viewToggle,
}) => {
  const organizationId = organization.get('id');
  const projectId = selectedProject.get('id');
  const {data: reports, status: queryStatus} = useProjectReports(projectId);
  const status: ReportsStatus =
    queryStatus === 'pending' ? 'loading' : queryStatus === 'error' ? 'error' : 'done';

  return (
    <PortfolioReportsView
      organizationId={organizationId}
      projectId={projectId}
      email={profile.get('email')}
      role={profile.get('role')}
      reports={reports ?? NO_REPORTS}
      status={status}
      pushNotification={pushNotification}
      viewToggle={viewToggle}
    />
  );
};

export const PortfolioReportsView: React.FunctionComponent<{
  organizationId: string;
  projectId: string;
  email: string;
  role: ApiOrganizationUser['role'];
  reports: ApiProjectReport[];
  status: ReportsStatus;
  pushNotification: PushNotificationFn;
  viewToggle?: React.ReactNode;
}> = ({organizationId, projectId, email, role, reports, status, pushNotification, viewToggle}) => {
  const location = useLocation();

  const [initialTableState] = React.useState(() => tableStateFromSearch(location.search));
  const {table} = usePortfolioReportsTable({
    reports,
    organizationId,
    projectId,
    initialFilters: initialTableState.filters,
    initialSortBy: initialTableState.sortBy,
  });

  useSyncTableStateToUrl(table, searchWithTableState);

  const [isExportDialogOpen, setIsExportDialogOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const selectedReports = React.useMemo(
    () => table.selectedFlatRows.map((row) => row.original),
    [table.selectedFlatRows]
  );

  return (
    <>
      <div className={cs.tableControls}>
        {viewToggle}
        <PortfolioReportsControls table={table} reports={reports} />
      </div>
      <PortfolioReportsTable
        table={table}
        status={status}
        hasReports={reports.length > 0}
        organizationId={organizationId}
        projectId={projectId}
        onExportSelected={() => setIsExportDialogOpen(true)}
        onDeleteSelected={role === 'readonly' ? undefined : () => setIsDeleteDialogOpen(true)}
      />
      <ReportExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        projectId={projectId}
        reports={selectedReports}
        email={email}
        onExported={() => {
          const count = selectedReports.length;
          pushNotification({
            message: `Exporting ${count === 1 ? '1 report' : `${count} reports`} to PDF. We’ll email ${email} when ${count === 1 ? 'it’s' : 'they’re'} ready.`,
          });
          table.toggleAllRowsSelected(false);
        }}
      />
      <ReportDeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        projectId={projectId}
        reports={selectedReports}
        onDeleted={(count) => {
          pushNotification({message: `Deleted ${count === 1 ? '1 report' : `${count} reports`}.`});
          table.toggleAllRowsSelected(false);
        }}
        onError={(error) =>
          pushNotification({
            message: error.message || 'Something went wrong deleting reports. Please try again.',
          })
        }
      />
    </>
  );
};

export default PortfolioReports;
